/* eslint-disable react/jsx-pascal-case */
import { Themed } from 'theme-ui'
import { useComponent } from '@uswitch/ev-utils/context/selectors'
import { toKebabCase } from '@uswitch/ev-utils/string'

import { BLOCKS as B, INLINES as I, MARKS as M } from '@contentful/rich-text-types'
import { LinkWithChevron as Link, Palette } from '@uswitch/ev-core/react'
import { moduleEntity } from '../helpers/modules'
import Filter from '../helpers/filtered'

import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'

const paletteHeading = as => (node, text) => (
  <Palette
    as={as}
    px={{ color: palette => palette.headingColor || palette.textColor }}
    id={toKebabCase(documentToPlainTextString(node) || '')}
  >
    {text}
  </Palette>
)

export const Options = (config = {}) => {
  const {
    observedWaypoints,
    optionOverrides = { renderMark: {}, renderNode: {} },
    ...extraData
  } = config
  const out = {
    renderMark: {
      [M.BOLD]: text => <Themed.b>{text}</Themed.b>,
      [M.ITALIC]: text => <Themed.i>{text}</Themed.i>,
      [M.UNDERLINE]: text => <span sx={{ textDecoration: 'underline' }}>{text}</span>,
      [M.CODE]: text => <Themed.strong>{text}</Themed.strong>,
      ...optionOverrides.renderMark,
    },

    renderNode: {
      [B.EMBEDDED_ASSET]: node =>
        moduleEntity(node.data.target, {
          ...extraData,
          spacing: node.spacing,
          tag: 'embeddedAsset',
        }),
      [B.EMBEDDED_ENTRY]: node => moduleEntity(node.data.target, extraData),
      [B.HEADING_1]: paletteHeading(Themed.h1),
      [B.HEADING_2]: (node, text) => {
        const id = toKebabCase(documentToPlainTextString(node) || '')

        return (
          <Palette
            as={Themed.h2}
            px={{ color: palette => palette.headingColor || palette.textColor }}
            {...(observedWaypoints && observedWaypoints[id]
              ? { ref: observedWaypoints[id][1] }
              : {})}
            id={id}
          >
            {text}
          </Palette>
        )
      },
      [B.HEADING_3]: paletteHeading(Themed.h3),
      [B.HEADING_4]: paletteHeading(Themed.h4),
      [B.HEADING_5]: paletteHeading(Themed.h5),
      [B.HEADING_6]: paletteHeading(Themed.h6),
      [B.HR]: () => <Themed.hr />,
      [B.UL_LIST]: (_, text) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [, List] = useComponent({ tag: 'list' })
        return <List listType='bullet'>{text}</List>
      },
      [B.OL_LIST]: (_, text) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [, List] = useComponent({ tag: 'list' })
        return <List listType='numeric'>{text}</List>
      },
      [B.LIST_ITEM]: (_, text) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [, ListItem] = useComponent({ tag: 'listItem' })
        return <ListItem>{text}</ListItem>
      },
      [I.HYPERLINK]: (node, text) => (
        <Filter when={text.join('').length}>
          <Link {...extraData} href={node.data.uri}>
            {text}
          </Link>
        </Filter>
      ),
      [B.PARAGRAPH]: (node, text) => (
        <Filter when={text.join('').length}>
          <Palette as='p' sx={{ variant: 'styles.p' }} px={{ color: 'textColor' }}>
            <For each='content' index='i' of={text}>
              {content}
              <If condition={typeof content === 'string' && content.endsWith('\n')}>
                <br />
              </If>
            </For>
          </Palette>
        </Filter>
      ),
      [B.QUOTE]: (node, text) => <Themed.blockquote>{text}</Themed.blockquote>,
      ...optionOverrides.renderNode,
    },
  }
  return out
}

export default Options
